"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type SegmentRow = {
  id: string
  fromStationId: string
  toStationId: string
  fromStationName: string
  fromStationCode: string
  toStationName: string
  toStationCode: string
  distanceKm: number
  lineNumber: string
  lineCategory: string
  isElectrified: boolean
  maxSpeedKmh: number | null
  maxAxleLoadTons: number | null
  isActive: boolean
}

type SegmentsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

const LINE_CATEGORY_VALUES = ['TRUNK', 'MAIN', 'SECONDARY', 'LOCAL'] as const

function mapSegmentRow(item: Record<string, unknown>): SegmentRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    fromStationId: typeof item.from_station_id === 'string' ? item.from_station_id : '',
    toStationId: typeof item.to_station_id === 'string' ? item.to_station_id : '',
    fromStationName: typeof item.from_station_name === 'string' ? item.from_station_name : '',
    fromStationCode: typeof item.from_station_code === 'string' ? item.from_station_code : '',
    toStationName: typeof item.to_station_name === 'string' ? item.to_station_name : '',
    toStationCode: typeof item.to_station_code === 'string' ? item.to_station_code : '',
    distanceKm: typeof item.distance_km === 'number' ? item.distance_km : 0,
    lineNumber: typeof item.line_number === 'string' ? item.line_number : '',
    lineCategory: typeof item.line_category === 'string' ? item.line_category : '',
    isElectrified: typeof item.is_electrified === 'boolean' ? item.is_electrified : true,
    maxSpeedKmh: typeof item.max_speed_kmh === 'number' ? item.max_speed_kmh : null,
    maxAxleLoadTons: typeof item.max_axle_load_tons === 'number' ? item.max_axle_load_tons : null,
    isActive: typeof item.is_active === 'boolean' ? item.is_active : true,
  }
}

export default function RailNetworkSegmentsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'rail-network-segments-list',
  })
  const [rows, setRows] = React.useState<SegmentRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()

  const getLineCategoryLabel = React.useCallback((lineCategory: string) => {
    const fallbackMap: Record<string, string> = {
      TRUNK: 'A - Trunk (Magistralna)',
      MAIN: 'B - Main (Pierwszorzędna)',
      SECONDARY: 'C - Secondary (Drugorzędna)',
      LOCAL: 'D - Local (Znaczenia miejscowego)',
    }
    return t(`rail_network.segments.categories.${lineCategory}`, fallbackMap[lineCategory] ?? lineCategory)
  }, [t])

  const getBooleanLabel = React.useCallback((value: boolean) => {
    return value
      ? t('rail_network.common.yes', 'Yes')
      : t('rail_network.common.no', 'No')
  }, [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'lineCategory',
      label: t('rail_network.segments.filters.lineCategory', 'Line Category'),
      type: 'select' as const,
      options: LINE_CATEGORY_VALUES.map((value) => ({ value, label: getLineCategoryLabel(value) })),
    },
    {
      id: 'lineNumber',
      label: t('rail_network.segments.filters.lineNumber', 'Line Number'),
      type: 'text' as const,
    },
  ], [getLineCategoryLabel, t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const lineCategory = filterValues.lineCategory
    if (typeof lineCategory === 'string' && lineCategory.trim()) params.set('lineCategory', lineCategory)
    const lineNumber = filterValues.lineNumber
    if (typeof lineNumber === 'string' && lineNumber.trim()) params.set('lineNumber', lineNumber)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: SegmentsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<SegmentsResponse>(
          `/api/rail_network/segments?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('rail_network.segments.error.load', 'Failed to load segments')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapSegmentRow).filter((row): row is SegmentRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('rail_network.segments.error.load', 'Failed to load segments')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (segment: SegmentRow) => {
    if (!segment.id) return
    const confirmed = await confirm({
      title: t('rail_network.segments.deleteConfirm', 'Delete this segment?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/rail_network/segments?id=${encodeURIComponent(segment.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('rail_network.segments.deleteError', 'Failed to delete segment') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== segment.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('rail_network.segments.deleteSuccess', 'Segment deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('rail_network.segments.deleteError', 'Failed to delete segment')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<SegmentRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'fromStationName',
        header: t('rail_network.segments.columns.fromStation', 'From Station'),
        cell: ({ row }) => row.original.fromStationName
          ? `${row.original.fromStationName} (${row.original.fromStationCode})`
          : noValue,
      },
      {
        accessorKey: 'toStationName',
        header: t('rail_network.segments.columns.toStation', 'To Station'),
        cell: ({ row }) => row.original.toStationName
          ? `${row.original.toStationName} (${row.original.toStationCode})`
          : noValue,
      },
      {
        accessorKey: 'lineNumber',
        header: t('rail_network.segments.columns.lineNumber', 'Line'),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.lineNumber}</span>,
      },
      {
        accessorKey: 'lineCategory',
        header: t('rail_network.segments.columns.lineCategory', 'Category'),
        cell: ({ row }) => getLineCategoryLabel(row.original.lineCategory),
      },
      {
        accessorKey: 'distanceKm',
        header: t('rail_network.segments.columns.distance', 'Distance (km)'),
        cell: ({ row }) => `${row.original.distanceKm} km`,
      },
      {
        accessorKey: 'isElectrified',
        header: t('rail_network.segments.columns.electrified', 'Electrified'),
        cell: ({ row }) => getBooleanLabel(row.original.isElectrified),
      },
      {
        accessorKey: 'maxSpeedKmh',
        header: t('rail_network.segments.columns.maxSpeed', 'Max Speed'),
        cell: ({ row }) => row.original.maxSpeedKmh !== null ? `${row.original.maxSpeedKmh} km/h` : noValue,
      },
      {
        accessorKey: 'maxAxleLoadTons',
        header: t('rail_network.segments.columns.axleLoad', 'Max Axle Load'),
        cell: ({ row }) => row.original.maxAxleLoadTons !== null ? `${row.original.maxAxleLoadTons} t` : noValue,
      },
    ]
  }, [getBooleanLabel, getLineCategoryLabel, t])

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex items-center gap-2">
          <Link href="/backend/rail-network">
            <Button variant="outline" size="sm">
              {t('rail_network.nav.stations', 'Stations')}
            </Button>
          </Link>
        </div>
        <DataTable<SegmentRow>
          title={t('rail_network.segments.title', 'Rail Segments')}
          refreshButton={{
            label: t('rail_network.segments.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('rail_network.segments.searchPlaceholder', 'Search segments...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'rail_network.segments.list' }}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('rail_network.segments.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
